import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Star as StarIcon,
  Public as PublicIcon,
} from '@mui/icons-material';

import { nodeEditorService } from '../../services/nodeEditorService';
import { NodeEditorPrompt } from '../../types/nodeEditor.types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => {
  return value === index ? <Box sx={{ p: 2 }}>{children}</Box> : null;
};

export const PromptManager: React.FC = () => {
  const [prompts, setPrompts] = useState<NodeEditorPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<NodeEditorPrompt | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    prompt_text: '',
    description: '',
    prompt_type: 'user',
    category: '',
    is_public: false,
  });
  
  useEffect(() => {
    loadPrompts();
  }, []);
  
  const loadPrompts = async () => {
    setLoading(true);
    try {
      const data = await nodeEditorService.getPrompts();
      setPrompts(data);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      if (editingPrompt) {
        await nodeEditorService.updatePrompt(editingPrompt.id, formData);
      } else {
        await nodeEditorService.createPrompt(formData);
      }
      setShowDialog(false);
      loadPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
    }
  };
  
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    try {
      await nodeEditorService.deletePrompt(id);
      loadPrompts();
    } catch (error) {
      console.error('Failed to delete prompt:', error);
    }
  };
  
  const handleCopy = (prompt: NodeEditorPrompt) => {
    setEditingPrompt(null);
    setFormData({
      name: `${prompt.name} (Copy)`,
      prompt_text: prompt.prompt_text,
      description: prompt.description,
      prompt_type: prompt.prompt_type,
      category: prompt.category,
      is_public: false,
    });
    setShowDialog(true);
  };
  
  const openDialog = (prompt?: NodeEditorPrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        name: prompt.name,
        prompt_text: prompt.prompt_text,
        description: prompt.description,
        prompt_type: prompt.prompt_type,
        category: prompt.category,
        is_public: prompt.is_public,
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        name: '',
        prompt_text: '',
        description: '',
        prompt_type: 'user',
        category: '',
        is_public: false,
      });
    }
    setShowDialog(true);
  };
  
  const filteredPrompts = prompts.filter(p => {
    const matchesSearch = 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.prompt_text.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === 0) return matchesSearch; // All
    if (activeTab === 1) return matchesSearch && p.is_preset; // Presets
    if (activeTab === 2) return matchesSearch && p.prompt_type === 'quick'; // Quick Links
    if (activeTab === 3) return matchesSearch && p.is_system; // System
    return matchesSearch;
  });
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Prompt Manager</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog()}
          >
            New Prompt
          </Button>
        </Box>
        
        <TextField
          fullWidth
          size="small"
          placeholder="Search prompts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>
      
      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
        <Tab label="All" />
        <Tab label="Presets" />
        <Tab label="Quick Links" />
        <Tab label="System" />
      </Tabs>
      
      {/* Prompt List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <List>
          {filteredPrompts.map((prompt) => (
            <Paper key={prompt.id} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1">{prompt.name}</Typography>
                    {prompt.is_preset && <Chip size="small" label="Preset" color="secondary" />}
                    {prompt.is_system && <Chip size="small" label="System" color="error" />}
                    {prompt.is_public && (
                      <Tooltip title="Public">
                        <PublicIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                  </Box>
                  
                  {prompt.description && (
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {prompt.description}
                    </Typography>
                  )}
                  
                  <Typography
                    variant="body2"
                    sx={{
                      bgcolor: 'grey.100',
                      p: 1,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      maxHeight: 100,
                      overflow: 'auto',
                    }}
                  >
                    {prompt.prompt_text}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip size="small" label={prompt.prompt_type} />
                    {prompt.category && <Chip size="small" label={prompt.category} variant="outlined" />}
                    <Typography variant="caption" color="textSecondary">
                      Used {prompt.usage_count} times
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <IconButton onClick={() => openDialog(prompt)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleCopy(prompt)}>
                    <CopyIcon />
                  </IconButton>
                  {!prompt.is_preset && !prompt.is_system && (
                    <IconButton onClick={() => handleDelete(prompt.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </List>
        
        {filteredPrompts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No prompts found
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrompt ? 'Edit' : 'New'} Prompt</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.prompt_type}
              onChange={(e) => setFormData({ ...formData, prompt_type: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="quick">Quick Link</MenuItem>
              <MenuItem value="template">Template</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            label="Category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            margin="normal"
            placeholder="e.g., Frontend, Backend, Validation"
          />
          
          <TextField
            fullWidth
            label="Prompt Text"
            value={formData.prompt_text}
            onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
            margin="normal"
            multiline
            rows={10}
            required
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_public}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              />
            }
            label="Make public to organization"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptManager;
